"use client"

import * as React from "react"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastProps,
} from "@/components/ui/toast"

// Pre-computed base classes for better performance
const TOAST_CONTENT_CONTAINER_CLASSES = "grid gap-1";

// Memoized individual toast component for better performance
const ToastItem = React.memo<ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}>(({ id, title, description, action, ...props }) => {
  return (
    <Toast key={id} {...props}>
      <div className={TOAST_CONTENT_CONTAINER_CLASSES}>
        {title && <ToastTitle>{title}</ToastTitle>}
        {description && (
          <ToastDescription>{description}</ToastDescription>
        )}
      </div>
      {action}
      <ToastClose />
    </Toast>
  );
});
ToastItem.displayName = "ToastItem";

export const Toaster = React.memo(() => {
  const { toasts } = useToast();

  const renderedToasts = React.useMemo(() => 
    toasts.map((toast) => (
      <ToastItem
        key={toast.id}
        {...toast}
      />
    )),
    [toasts]
  );

  return (
    <ToastProvider>
      {renderedToasts}
      <ToastViewport />
    </ToastProvider>
  );
});

Toaster.displayName = "Toaster";
